/**
 * pollChapterSynthesis.js — helper for the scribe's New Episode flow.
 *
 * After a text-mode chapter is published, the upload response carries
 * `{synthesis_status: "pending", tts_job_id}`. Audio synthesis on the
 * upstream TTS server can take minutes for long chapters, so the
 * publish request itself returns immediately and the frontend polls
 * this endpoint until the audio lands.
 *
 * Each poll request is ~50ms (one upstream `/status` hit + maybe one
 * download/persist on done), so the 60s ingress timeout is never
 * threatened. We poll every 2s per the user spec.
 *
 * Usage:
 *   const finalAudio = await pollChapterSynthesis({
 *     kind: 'audiobook' | 'cinematic',
 *     id: chapterId,
 *     onProgress: ({attempt, elapsedMs}) => {...},
 *   });
 */
import { authFetch } from './authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export async function pollChapterSynthesis({
  kind,            // 'audiobook' | 'cinematic'
  id,
  pollIntervalMs = 3000,
  maxWallMs = 10 * 60 * 1000, // 10 min — generous for very long chapters
  onProgress,
} = {}) {
  if (!id) throw new Error('id is required');
  const url =
    kind === 'cinematic'
      ? `${BACKEND_URL}/api/cinematic/episode/${id}/synthesis-status`
      : `${BACKEND_URL}/api/audiobook/chapter/${id}/synthesis-status`;

  const startedAt = Date.now();
  let attempt = 0;
  while (Date.now() - startedAt < maxWallMs) {
    attempt += 1;
    let r;
    try {
      r = await authFetch(url);
    } catch {
      // transient — keep polling
      onProgress?.({ status: 'network-blip', attempt, elapsedMs: Date.now() - startedAt });
      await new Promise((rs) => setTimeout(rs, pollIntervalMs));
      continue;
    }
    const data = await r.json().catch(() => ({}));
    if (!r.ok) {
      throw new Error(data.detail || data.error || `HTTP ${r.status}`);
    }
    onProgress?.({
      status: data.synthesis_status,
      attempt,
      elapsedMs: Date.now() - startedAt,
      ...data,
    });
    if (data.synthesis_status === 'done' || data.synthesis_status === 'not_required') {
      return data;
    }
    if (data.synthesis_status === 'error') {
      throw new Error(data.error || 'TTS synthesis failed on the server');
    }
    await new Promise((rs) => setTimeout(rs, pollIntervalMs));
  }
  throw new Error('Chapter synthesis timed out');
}
